import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, Check, X, Loader2 } from "lucide-react";

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", reject);
    img.src = url;
  });
}

async function getCroppedBlob(src, area, { maxW, maxH, type, quality }) {
  const image = await createImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const scale = Math.min(1, maxW / area.width, maxH / area.height);
  canvas.width = Math.max(1, Math.round(area.width * scale));
  canvas.height = Math.max(1, Math.round(area.height * scale));
  if (type === "image/jpeg") { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  return new Promise((res) => canvas.toBlob((b) => res(b), type, quality));
}

const PRESETS = {
  logo: { aspect: 1, maxW: 512, maxH: 512, type: "image/png", quality: 0.92, cropShape: "rect", label: "Recadrer le logo" },
  banner: { aspect: 16 / 9, maxW: 1280, maxH: 720, type: "image/jpeg", quality: 0.9, cropShape: "rect", label: "Recadrer la bannière" },
};

export default function CropModal({ file, kind = "logo", onCancel, onDone }) {
  const cfg = PRESETS[kind] || PRESETS.logo;
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [previewArea, setPreviewArea] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((area, pixels) => { setAreaPixels(pixels); setPreviewArea(area); }, []);

  const previewStyle = previewArea ? (() => {
    const scale = 100 / previewArea.width;
    return {
      position: "absolute", width: `${scale * 100}%`, maxWidth: "none",
      transform: `translate(${-previewArea.x * scale}%, ${-previewArea.y * scale}%)`,
      transformOrigin: "top left",
    };
  })() : {};

  const confirm = async () => {
    if (!areaPixels || !src) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(src, areaPixels, cfg);
      const ext = cfg.type === "image/png" ? "png" : "jpg";
      const out = new File([blob], `${kind}.${ext}`, { type: cfg.type });
      await onDone(out);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid="crop-modal">
        <div className="px-5 py-3 border-b border-border">
          <h3 className="font-heading font-bold">{cfg.label}</h3>
          <p className="text-xs text-muted-foreground">Ajustez le cadrage et le zoom pour un rendu net.</p>
        </div>
        <div className="relative w-full h-72 bg-slate-900">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={cfg.aspect}
              cropShape={cfg.cropShape}
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        {previewArea && src && (
          <div className="flex items-center gap-3 px-5 py-3 border-t border-border bg-secondary/30" data-testid="crop-preview">
            <div className={`relative overflow-hidden bg-muted shrink-0 ${kind === "logo" ? "w-14 h-14 rounded-full border-2 border-white shadow" : "w-24 rounded-lg border border-border"}`} style={kind === "logo" ? {} : { aspectRatio: "16 / 9" }}>
              <img src={src} alt="aperçu" style={previewStyle} />
            </div>
            <p className="text-xs text-muted-foreground">{kind === "logo" ? "Aperçu du logo tel qu'il apparaîtra sur les cartes." : "Aperçu de la bannière."}</p>
          </div>
        )}
        <div className="flex items-center gap-3 px-5 py-3 border-t border-border">
          <ZoomIn size={16} className="text-muted-foreground shrink-0" />
          <input type="range" min="1" max="3" step="0.05" value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            data-testid="crop-zoom" aria-label="Zoom" className="flex-1 accent-rdcblue cursor-pointer" />
        </div>
        <div className="flex items-center justify-end gap-2 px-5 pb-4">
          <Button variant="outline" onClick={onCancel} data-testid="crop-cancel"><X size={15} className="mr-1" /> Annuler</Button>
          <Button onClick={confirm} disabled={saving} data-testid="crop-confirm">
            {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Check size={15} className="mr-1" />} Appliquer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
